import { ScrollArea } from '~/primitives/scroll-area';
import TabSwitch from '~/partials/TabSwitch';
import {
    createFileRoute,
    Outlet,
    stripSearchParams,
    useLoaderData,
} from '@tanstack/react-router';
import {
    defaultLeaderboardParams,
    leaderboardParamsSchema,
} from './-SearchParams';
import { useList } from '~/util/hooks/useList';
import { LeaderboardSettingsModal } from './-LeaderboardFilters';
import { useState } from 'react';
import { Filter } from 'lucide-react';
import { cn, seo } from '~/util';
import Paginator from '~/components/Paginator';
import Search from '~/partials/Search';
import { useRelativetime } from '~/util/hooks/useRelativeTime';

export const Route = createFileRoute('/leaderboard')({
    validateSearch: leaderboardParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultLeaderboardParams)],
    },
    head: ({
        match: {
            search: { type, country, search, page, sort, clan },
        },
    }) => ({
        meta: [
            {
                name: 'robots',
                content:
                    !import.meta.env.VITE_DEV_PREVIEW &&
                    country == defaultLeaderboardParams.country &&
                    search == defaultLeaderboardParams.search &&
                    page == defaultLeaderboardParams.page &&
                    sort == defaultLeaderboardParams.sort &&
                    clan == defaultLeaderboardParams.clan
                        ? 'index, follow'
                        : 'noindex, follow',
            },
            ...seo({
                title: 'Leaderboard',
            }),
        ],
        links: [
            {
                rel: 'canonical',
                href: '/leaderboard' + (type != 'users' ? `?type=${type}` : ''),
            },
        ],
    }),
    component: Leaderboard,
});

function Leaderboard() {
    const list = useList();

    const { type, search } = Route.useSearch({
        select: ({ type, search }) => ({ type, search }),
    });

    const { last_refreshed, page, pages } =
        useLoaderData({
            from: '/leaderboard/',
        }) ?? {};

    const navigate = Route.useNavigate();
    const relativeTime = useRelativetime(last_refreshed);
    const [modalOpen, setModalOpen] = useState(false);

    const leaderboardTypeSelector = (
        <TabSwitch
            current={type}
            options={['users', 'countries', 'clans']}
            onChange={(value) => {
                navigate({
                    search: (prev) => ({
                        ...prev,
                        type: value,
                        page: 1,
                    }),
                });
            }}
            optionsDisplay={['Users', 'Countries', 'Clans']}
            size="lg"
        />
    );

    return (
        <div className="bg-background-dark rounded-2xl py-4 clamp-[px-2-12-clamp]">
            <div className="flex flex-col lg:flex-row items-center justify-between mr-3">
                <div className="flex flex-col lg:flex-row items-center lg:gap-12">
                    <div className="flex items-center">
                        <h1 className="text-secondary-foreground clamp-[text-5xl-6xl-clamp] font-semibold">
                            Leaderboard
                        </h1>
                    </div>
                    <TabSwitch
                        options={['classic', 'platformer']}
                        current={list}
                        onChange={(list) =>
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    list,
                                    page: 1,
                                }),
                            })
                        }
                        className="my-2"
                        size="lg"
                    />
                </div>
                <div className="hidden lg:flex self-end mr-4">
                    Last updated {relativeTime}
                </div>
            </div>
            <ScrollArea className="w-full h-[calc(100vh-230px)]">
                <div className="mr-4">
                    <div className="relative w-full flex flex-col gap-2">
                        <div className="flex mx-auto lg:hidden">
                            {leaderboardTypeSelector}
                        </div>
                        <div className="flex w-full justify-between items-center py-2">
                            <div className="flex w-full gap-4">
                                <Search
                                    initial_input={search}
                                    onInputChange={(value: string) =>
                                        navigate({
                                            search: (prev) => ({
                                                ...prev,
                                                search: value,
                                                page: 1,
                                            }),
                                        })
                                    }
                                />
                                <div className="flex gap-2 items-center">
                                    <Filter
                                        size={40}
                                        className="mx-2 transition-colors text-secondary-light hover:text-secondary-foreground cursor-pointer"
                                        onClick={() => setModalOpen(true)}
                                    />
                                    <div className="hidden lg:block">
                                        {leaderboardTypeSelector}
                                    </div>
                                </div>
                            </div>
                            <div
                                className={cn(
                                    'flex items-center space-x-2 py-4 pl-4',

                                    'hidden md:flex',
                                )}
                            >
                                <Paginator
                                    currentPage={page}
                                    totalPages={pages}
                                    onPageChange={(page) =>
                                        navigate({
                                            search: (prev) => ({
                                                ...prev,
                                                page,
                                            }),
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <Outlet />
                        <LeaderboardSettingsModal
                            isOpen={modalOpen}
                            onClose={() => setModalOpen(false)}
                        />
                    </div>
                </div>
            </ScrollArea>
        </div>
    );
}
